// ================================================================
// useTranslation — Anbindung der React-Inseln an die i18n-Registry
// ================================================================
// Die Sprache ist ein Zustand AUSSERHALB von React (i18n/index.js). Genau dafür
// gibt es useSyncExternalStore: React abonniert die Quelle und rendert neu, wenn
// sie sich ändert. Kein Context, kein Provider, kein Prop-Durchreichen.
//
// Warum überhaupt, wenn CompanyDashboard bei LANGUAGE_CHANGED ohnehin draw() ruft?
// draw() ersetzt `container.innerHTML` und mountet die Inseln neu — heute würden sie
// die Sprache also auch ohne Hook mitbekommen. Das ist aber ein SEITENEFFEKT des
// Vanilla-Redraws, kein Vertrag: sobald eine Insel ausserhalb von #cd-root hängt
// (wie die AssistantBar) oder React eigenes Routing bekommt (docs/react-migration-plan.md),
// bricht das lautlos. Mit dem Hook ist jede Komponente für sich korrekt.
//
// Nutzung:
//   const t = useTranslation();
//   <button>{t('common.save')}</button>
//   <span>{t('settings.holidays.headYear', { year: 2026 })}</span>
import { useSyncExternalStore } from 'react';
import { events, EVENTS } from '../../core/events.js';
import { t as translate, getLanguage } from '../../i18n/index.js';

type Params = Record<string, string | number>;
type TFunction = (key: string, params?: Params) => string;

// events.on() liefert die Unsubscribe-Funktion zurück — genau die braucht
// useSyncExternalStore. NIEMALS events.off(EVENTS.LANGUAGE_CHANGED) benutzen:
// das löscht ALLE Listener dieses Events, auch den, mit dem CompanyDashboard neu
// zeichnet. Eine unmountende Insel würde damit den Umschalter global lahmlegen.
function subscribe(onStoreChange: () => void): () => void {
  return events.on(EVENTS.LANGUAGE_CHANGED, onStoreChange);
}

// Snapshot = der Sprachcode ('de'/'en'). Ein String, also stabil vergleichbar —
// useSyncExternalStore rendert nur neu, wenn er sich wirklich ändert.
function getSnapshot(): string {
  return getLanguage();
}

/**
 * Gibt die Übersetzungsfunktion zurück und hält die Komponente bei
 * Sprachwechsel aktuell.
 */
export function useTranslation(): TFunction {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return translate as TFunction;
}
